
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X, LogOut, Settings, User, ChevronRight, Home, BookOpen, FileText, Info, MessageSquare, Briefcase, LinkIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { OptimizedImage } from "@/components/ui/image";
import { supabase } from "@/integrations/supabase/client";

const isDevelopment = import.meta.env.DEV;
const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  }
};

interface NavLink {
  id?: string;
  path: string;
  label: string;
  visible: boolean;
  order: number;
  icon: JSX.Element;
}

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navigationItems, setNavigationItems] = useState<NavLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const {
    user,
    signOut,
    hasRole
  } = useAuth();
  const location = useLocation();

  // Default navigation items with icons
  const defaultNavigationItems: NavLink[] = [
    { path: "/", label: "Home", visible: true, order: 0, icon: <Home className="h-4 w-4 mr-2" /> },
    { path: "/about", label: "About", visible: true, order: 1, icon: <Info className="h-4 w-4 mr-2" /> },
    { path: "/courses", label: "Courses", visible: true, order: 2, icon: <BookOpen className="h-4 w-4 mr-2" /> },
    { path: "/blog", label: "Blog", visible: true, order: 3, icon: <FileText className="h-4 w-4 mr-2" /> },
    { path: "/freelance-toolkit", label: "Toolkit", visible: true, order: 4, icon: <Briefcase className="h-4 w-4 mr-2" /> },
    { path: "/contact", label: "Contact", visible: true, order: 5, icon: <MessageSquare className="h-4 w-4 mr-2" /> }
  ];

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchNavigationLinks();
  }, []);

  const fetchNavigationLinks = async () => {
    try {
      setIsLoading(true);
      
      // Try to get navigation settings from database
      const { data, error } = await supabase
        .from('navigation_links')
        .select('*')
        .order('order', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        // Merge the database data with our default items to get the icons
        const mergedNavItems = data.map(item => {
          const defaultItem = defaultNavigationItems.find(defItem => defItem.path === item.path);
          return {
            ...item,
            icon: defaultItem?.icon || <LinkIcon className="h-4 w-4 mr-2" />
          };
        });
        
        setNavigationItems(mergedNavItems);
      } else {
        // If no database entries exist, use the defaults
        setNavigationItems(defaultNavigationItems);
      }
    } catch (error) {
      console.error("Error fetching navigation settings:", error);
      // Fallback to default navigation if there's an error
      setNavigationItems(defaultNavigationItems);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  // Filter navigation items to only show visible ones
  const visibleNavigationItems = navigationItems.filter(item => item.visible);

  return <header className={cn("sticky top-0 z-50 transition-all duration-300", scrolled ? "bg-white/90 dark:bg-background/90 backdrop-blur-md shadow-md" : "bg-white/80 dark:bg-background/80 backdrop-blur-sm border-b")}>
      <div className="container flex h-16 items-center">
        <div className="mr-4 flex items-center justify-center h-full">
          <Link to="/" className="flex items-center justify-center">
            <OptimizedImage
              src="/lovable-uploads/0b8768c3-e86a-40d9-a11e-7fb5e194c5af.png" 
              alt="Freelance With Andy Logo" 
              className="h-[40px] w-auto object-contain"
              width={161}
              height={40}
            />
          </Link>
        </div>
        
        <div className="hidden md:flex items-center space-x-1">
          {visibleNavigationItems.map(item => <Link key={item.path} to={item.path} className={cn("px-3 py-2 rounded-md text-sm font-medium transition-colors", location.pathname === item.path ? "bg-primary/10 text-primary" : "text-gray-700 dark:text-gray-200 hover:bg-primary/5 hover:text-primary")}>
              {item.label}
            </Link>)}
        </div>
        
        <div className="flex items-center ml-auto">
          {user ? <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8 ring-2 ring-primary/20">
                    <AvatarImage src={user?.user_metadata?.avatar_url || undefined} alt={user?.email || "User"} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {user?.user_metadata?.first_name?.[0]?.toUpperCase() || user?.user_metadata?.last_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.user_metadata?.first_name || ""} {user?.user_metadata?.last_name || ""}
                      {!user?.user_metadata?.first_name && !user?.user_metadata?.last_name && user?.email}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center w-full">
                      <User className="mr-2 h-4 w-4 text-primary" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  {hasRole("admin") && <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center w-full">
                        <Settings className="mr-2 h-4 w-4 text-primary" />
                        <span>Admin Dashboard</span>
                      </Link>
                    </DropdownMenuItem>}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500 focus:text-red-500">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu> : <div className="flex items-center space-x-2">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-gray-700 hover:text-primary hover:bg-primary/5">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Sign Up
                </Button>
              </Link>
            </div>}
          
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden ml-2">
              <Button variant="ghost" size="icon">
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px] border-l border-primary/10 pr-0">
              <div className="flex flex-col space-y-2 mt-8 -mr-4">
                {visibleNavigationItems.map(item => <Link key={item.path} to={item.path} className={cn("flex items-center px-4 py-3 hover:bg-primary/5 rounded-l-md transition-colors", location.pathname === item.path ? "bg-primary/10 text-primary border-r-4 border-primary" : "text-gray-700 dark:text-gray-200")}>
                    {item.icon}
                    <span>{item.label}</span>
                    {location.pathname === item.path && <ChevronRight className="ml-auto h-4 w-4" />}
                  </Link>)}
                
                {user ? <>
                    <div className="my-2 border-t border-gray-100 dark:border-gray-800"></div>
                    <Link to="/profile" className={cn("flex items-center px-4 py-3 hover:bg-primary/5 rounded-l-md transition-colors", location.pathname === "/profile" ? "bg-primary/10 text-primary border-r-4 border-primary" : "text-gray-700 dark:text-gray-200")}>
                      <User className="h-4 w-4 mr-2" />
                      <span>Profile</span>
                      {location.pathname === "/profile" && <ChevronRight className="ml-auto h-4 w-4" />}
                    </Link>
                    
                    {hasRole("admin") && <Link to="/admin" className={cn("flex items-center px-4 py-3 hover:bg-primary/5 rounded-l-md transition-colors", location.pathname === "/admin" ? "bg-primary/10 text-primary border-r-4 border-primary" : "text-gray-700 dark:text-gray-200")}>
                        <Settings className="h-4 w-4 mr-2" />
                        <span>Admin Dashboard</span>
                        {location.pathname === "/admin" && <ChevronRight className="ml-auto h-4 w-4" />}
                      </Link>}
                    
                    <button onClick={handleLogout} className="flex items-center px-4 py-3 hover:bg-red-50 text-red-500 rounded-l-md transition-colors text-left">
                      <LogOut className="h-4 w-4 mr-2" />
                      <span>Logout</span>
                    </button>
                  </> : <div className="mt-4 px-4 space-y-2">
                    <Link to="/login">
                      <Button variant="outline" className="w-full justify-start">
                        Login
                      </Button>
                    </Link>
                    <Link to="/signup">
                      <Button className="w-full justify-start">
                        Sign Up
                      </Button>
                    </Link>
                  </div>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
};

export default Navbar;
